/**
 * 🔍 ERROR MONITORING SERVICE — Production Error Tracking
 *
 * Captures and reports errors to system_logs table.
 * Provides crash reporting, unhandled rejection handling,
 * and structured error context for debugging.
 *
 * Initialize once at app startup with `initErrorMonitoring()`.
 */

import { supabase } from '../lib/supabaseClient';

// ─── Types ────────────────────────────────────────────────────

type ErrorSeverity = 'error' | 'warn' | 'fatal';

interface ErrorReport {
  message: string;
  stack?: string;
  severity: ErrorSeverity;
  category: string;
  component?: string;
  url?: string;
  userId?: string;
  workspaceId?: string;
  metadata?: Record<string, unknown>;
}

interface ErrorContext {
  userId?: string;
  workspaceId?: string;
  organizationId?: string;
}

// ─── State ────────────────────────────────────────────────────

let _context: ErrorContext = {};
let _initialized = false;
const _errorBuffer: ErrorReport[] = [];
const BUFFER_FLUSH_INTERVAL = 10_000; // 10 seconds
const MAX_BUFFER_SIZE = 50;

// ─── Public API ───────────────────────────────────────────────

/**
 * Initialize error monitoring. Call once at app startup.
 */
export function initErrorMonitoring(): void {
  if (_initialized) return;
  _initialized = true;

  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    captureError(event.error || new Error(event.message), {
      category: 'unhandled_error',
      component: `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    captureError(error, {
      category: 'unhandled_rejection',
    });
  });

  // Flush buffer periodically
  setInterval(flushBuffer, BUFFER_FLUSH_INTERVAL);

  console.log('[ErrorMonitoring] Initialized');
}

/**
 * Set user/workspace context for error reports.
 */
export function setErrorContext(ctx: Partial<ErrorContext>): void {
  _context = { ..._context, ...ctx };
}

/**
 * Capture and report an error.
 */
export function captureError(
  error: Error | string,
  options: {
    severity?: ErrorSeverity;
    category?: string;
    component?: string;
    metadata?: Record<string, unknown>;
  } = {}
): void {
  const err = typeof error === 'string' ? new Error(error) : error;

  const report: ErrorReport = {
    message: err.message,
    stack: err.stack,
    severity: options.severity ?? 'error',
    category: options.category ?? 'runtime',
    component: options.component,
    url: window.location.pathname,
    userId: _context.userId,
    workspaceId: _context.workspaceId,
    metadata: options.metadata,
  };

  _errorBuffer.push(report);

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error(`[ErrorMonitoring] ${report.severity}:`, report.message, report);
  }

  // Flush immediately for fatal errors
  if (report.severity === 'fatal' || _errorBuffer.length >= MAX_BUFFER_SIZE) {
    flushBuffer();
  }
}

/**
 * Capture a warning (non-critical issue).
 */
export function captureWarning(
  message: string,
  metadata?: Record<string, unknown>
): void {
  captureError(message, { severity: 'warn', category: 'warning', metadata });
}

/**
 * Wrap an async function with error capture.
 */
export function withErrorCapture<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  category: string
): T {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), { category });
      throw err;
    }
  }) as T;
}

// ─── Internal ─────────────────────────────────────────────────

// Map internal category names to DB CHECK constraint values
const CATEGORY_MAP: Record<string, string> = {
  unhandled_error: 'system',
  unhandled_rejection: 'system',
  runtime: 'system',
  warning: 'system',
  auth: 'auth',
  workflow: 'workflow',
  api: 'api',
  integration: 'integration',
  system: 'system',
  billing: 'billing',
};

async function flushBuffer(): Promise<void> {
  if (_errorBuffer.length === 0) return;

  const batch = _errorBuffer.splice(0, MAX_BUFFER_SIZE);

  try {
    const rows = batch.map(report => ({
      level: report.severity === 'fatal' ? 'error' : report.severity,
      category: CATEGORY_MAP[report.category] ?? 'system',
      message: report.message,
      user_id: report.userId || null,
      workspace_id: report.workspaceId || null,
      metadata: {
        stack: report.stack?.slice(0, 2000),
        component: report.component,
        url: report.url,
        originalCategory: report.category,
        ...report.metadata,
      },
    }));

    await supabase.from('system_logs').insert(rows);
  } catch {
    // If logging fails, don't re-throw to avoid infinite loop
    if (import.meta.env.DEV) {
      console.warn('[ErrorMonitoring] Failed to flush error buffer to DB');
    }
  }
}
